import Api from 'src/services/api'
import unionBy from 'lodash/unionBy'
import cloneDeep from 'lodash/cloneDeep'
import groupBy from 'lodash/groupBy'
import { getFixedSessionName } from 'src/utils/utils'
import { decorateTimeslotsForXmas2025 } from 'src/services/filters/xmas2025'
import { trackIssueCustom } from 'src/boot/sentry'
import { date as dateUtils } from 'quasar'

const getAdjustedTimes = (targetTimes, altTimes) => {
  let output = []
  altTimes = altTimes.map((o) => ({ ...o, ghost: true }))
  output = unionBy(targetTimes, altTimes, 'time')
  output = output.sort((a, b) => {
    return new Date(a.time) - new Date(b.time)
  })
  return output
}

const getAdjustedSessions = (data = [], altData = []) => {
  let output = []
  if (!data.length || !altData.length) {
    return output
  }
  for (let session of data) {
    let matchedAltSession = altData.find(o => {
      return o.session_name.substring(0, 3) === session.session_name.substring(0, 3)
    })
    let adjustedSession = cloneDeep(session)
    if (matchedAltSession) {
      let times = getAdjustedTimes(session['seating_areas'][0]['times'], matchedAltSession['seating_areas'][0]['times'])
      adjustedSession.seating_areas[0]['times'] = times
    }
    output.push(adjustedSession)
  }
  return output
}

const state = {
  initTargetSid: null,
  targetSid: null,
  targetTimeslotsData: [],
  altSitesSource: [],
  loading: {
    sitesList: false,
    altSitesSource: false,
    targetTimeslotsData: false
  }
}

const getters = {
  adjustedTargetTimeslots: state => {
    let { targetTimeslotsData, altTimeslotsData } = state
    return getAdjustedSessions(targetTimeslotsData, altTimeslotsData)
  },
  adjustedAltTimeslots: state => {
    let { targetTimeslotsData, altTimeslotsData } = state
    return getAdjustedSessions(altTimeslotsData, targetTimeslotsData)
  },
  targetTimeslotsPreparedFlat: (state, getters, rootState, rootGetters) => {
    let data = []
    let { targetTimeslotsData, targetSid } = state
    const targetRestaurant = rootGetters.restaurantInfo(targetSid)
    const { min_atbl_timeslot, max_atbl_timeslot } = (targetRestaurant && targetRestaurant.extra) || {}

    const { locations: extraFlowLocations, timeslotsFilterCb } = rootState.app.extraFlow || {}

    let targetExtraFlowLocation
    if (extraFlowLocations && extraFlowLocations.length) {
      targetExtraFlowLocation = extraFlowLocations.find(loc => loc.id === targetSid)
    }

    targetTimeslotsData.forEach((o, sessionIndex) => {
      const firstSeatingArea = o.seating_areas[0]
      let times = firstSeatingArea.times
      times = times.filter(item => {
        let isAvailable = true
        if (process.env.DISABLE_SLOTS) {
          const timeslots = process.env.DISABLE_SLOTS.split(',')
          isAvailable = !(timeslots.some(v => item.time.includes(v)))
        }

        // Apply ranges specified via Central Admin interface (if exist)
        let cleanTime = item.time.split('+')[0]
        let ongoingDT = new Date(cleanTime)
        let ongoingT = dateUtils.formatDate(ongoingDT, 'HH:mm')
        if (min_atbl_timeslot) {
          // Source: https://stackoverflow.com/a/69234924
          if (ongoingT < min_atbl_timeslot) isAvailable = false
        }
        if (max_atbl_timeslot) {
          if (ongoingT > max_atbl_timeslot) isAvailable = false
        }
        if (targetExtraFlowLocation) {
          if (!item.is_available) isAvailable = false
          if (targetExtraFlowLocation.start_time && ongoingT < targetExtraFlowLocation.start_time) isAvailable = false
          if (targetExtraFlowLocation.end_time && ongoingT > targetExtraFlowLocation.end_time) isAvailable = false
          if (targetExtraFlowLocation.timeslotsFilterCb) {
            isAvailable = targetExtraFlowLocation.timeslotsFilterCb(item.time)
          }
        }

        if (timeslotsFilterCb) {
          isAvailable = timeslotsFilterCb(item.time)
        }

        return isAvailable && !item.online_suspended
      })

      if (times.length) {
        data.push(
          ...times.map(item => ({
            time: item.time,
            // DEBUG: Simulate unavailable slots for 18:xx times (we use this for testing purposes)
            // is_available: item.time.includes('T18:') ? false : item.is_available,
            is_available: item.is_available,
            seating_area_uid: firstSeatingArea.seating_area_uid,
            session_name: getFixedSessionName(item.time)
          }))
        )
      }
    })

    const targetDate = data && data[0] && data[0].time

    // +++ Custom logic for Loetje Amsterdam de Pijp on 2024-05-27
    if (targetSid === '31c1d494-f503-4012-9e28-d489b34b8c64' &&
      (targetDate && targetDate.includes('2024-05-27'))
    ) {
      data = []
    }
    // +++

    const reservationForm = rootGetters.reservationForm || {}
    data = decorateTimeslotsForXmas2025(data, {
      date: reservationForm.date || targetDate,
      partySize: reservationForm.party_size
    })

    data = data.reverse()
    return data
  },
  targetTimeslotsPreparedGrouped: (state, getters) => {
    let targetTimeslotsPreparedFlat = getters.targetTimeslotsPreparedFlat
    let data = groupBy(targetTimeslotsPreparedFlat, 'session_name')
    for (const [sessionKey, times] of Object.entries(data)) {
      data[sessionKey] = {
        times: times.reverse(),
        timeslotsCount: times.length
      }
    }
    return data
  },
  altSites (state, getters, rootState, rootGetters) {
    let allRestaurants = rootGetters.allRestaurants
    return state.altSitesSource.filter(o => {
      return allRestaurants.find(item => item.id === o.id)
    })
  }
}

const mutations = {
  SET_INIT_TARGET_SID (state, payload) {
    state.initTargetSid = payload
  },
  SET_TARGET_SID (state, payload) {
    state.targetSid = payload
  },
  SET_TARGET_TIMESLOTS_DATA (state, payload) {
    let sessions = payload
      ? payload.days[0].sessions
      : []
    state.targetTimeslotsData = sessions
  },
  SET_ALT_SITES_SOURCE (state, payload) {
    state.altSitesSource = payload
  },
  SET_LOADING (state, payload) {
    let { key, value } = payload
    if (state.loading.hasOwnProperty(key)) {
      state.loading[key] = !!value
    }
  }
}

const actions = {
  init ({ commit, getters }, payload) {
    commit('SET_TARGET_SID', payload)
  },
  fetchTargetAvailability ({ state, commit }, payload) {
    commit('SET_LOADING', { key: 'targetTimeslotsData', value: true })
    return Api.getReservationsAvailability(state.targetSid, payload)
      .then(
        ({ data }) => {
          let times = []
          let { sessions } = data.days[0] || {}

          if (sessions && sessions.length) {
            sessions.forEach(session => {
              let thisTimes = session.seating_areas && session.seating_areas[0].times
              thisTimes = thisTimes.filter(item => !item.online_suspended)
              times = [
                ...times,
                ...thisTimes
              ]
            })
          }

          commit('SET_TARGET_TIMESLOTS_DATA', (times && times.length) ? data : null)
          return data
        },
        ({ response = {} }) => {
          commit('SET_TARGET_TIMESLOTS_DATA', null)
          return response
        }
      )
      .then(data => {
        commit('SET_LOADING', { key: 'targetTimeslotsData', value: false })
        return data
      })
  },
  fetchAlternativeSites ({ commit, dispatch }, payload) {
    let { query } = payload
    commit('SET_LOADING', { key: 'sitesList', value: true })
    return Api.fetchSitesList(query)
      .then(
        ({ data }) => {
          data.shift()
          return dispatch('fillAlternativeSitesList', { sites: data, query })
        },
        ({ response = {} }) => {
          return response.data
        }
      )
      .then(data => {
        commit('SET_LOADING', { key: 'sitesList', value: false })
        return data
      })
  },
  fillAlternativeSitesList ({ state, commit }, payload = {}) {
    let { sites, query } = payload
    sites = sites.filter(o => o.extra && !o.extra.exclude_from_atbl)
    let promises = sites.map((o) => {
      return Api.getReservationsAvailability(o.id, {
        date: query.date,
        party_size: query.party_size
      }, {
        customParams: { sid: o.id }
      })
        .catch(({ response }) => {
          return response
        })
    })

    commit('SET_LOADING', { key: 'altSitesSource', value: true })
    return Promise.all(promises)
      .then((responses = []) => {
        responses = responses.filter(o => (o && o.status) === 200)

        let sessionsBySite = responses.map(({ data, config }) => {
          // const customParams = config.customParams
          let { sessions } = data.days[0] || {}
          const urlParts = config.url.split('/')
          const sid = urlParts[urlParts.lastIndexOf('sites') + 1]
          return {
            // sid: customParams.sid,
            sid: sid,
            sessions
          }
        })
        sessionsBySite = sessionsBySite.filter(o => {
          let times = o.sessions && o.sessions[0].seating_areas && o.sessions[0].seating_areas[0].times
          times = times.filter(item => (item.is_available && !item.online_suspended))

          // Apply Christmas decorator to filter out unavailable timeslots
          const firstTime = times && times[0] && times[0].time
          times = decorateTimeslotsForXmas2025(times, {
            date: firstTime,
            partySize: query.party_size
          })

          return times && times.length
          // return !!(o.sessions && o.sessions[0].seating_areas && o.sessions[0].seating_areas[0].times)
        })

        let altSitesList = sites.filter(site => {
          return sessionsBySite.find(o => site.id === o.sid)
        })
        commit('SET_ALT_SITES_SOURCE', altSitesList)
      }, (e) => {
        trackIssueCustom({
          section: 'Generic API',
          title: 'Generic API: getting availabilities for alternatives: global CATCH',
          tags: {
            //
          },
          data: e
        })
      })
      .then(data => {
        commit('SET_LOADING', { key: 'altSitesSource', value: false })
        return data
      })
  },
  setTargetSid ({ commit, getters }, payload) {
    commit('SET_TARGET_SID', payload)
  },
  setTargetData ({ commit, getters }, payload = '') {
    commit('SET_INIT_TARGET_SID', payload)
    commit('SET_TARGET_SID', payload)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
